<script lang="ts">
  import { onMount } from 'svelte';
  import { setupProjectAnimations, animateValue } from '$lib/utils/animations';
  import Nav from '$lib/components/Nav.svelte';
  import ProjectNav from '$lib/components/ProjectNav.svelte';
  import Footer from '$lib/components/Footer.svelte';
  import IndustryTag from '$lib/components/IndustryTag.svelte';
  import VideoPlayer from '$lib/components/VideoPlayer.svelte';
  import FeatureCell from '$lib/components/FeatureCell.svelte';
  import ProjectHeroSplit from '$lib/components/ProjectHeroSplit.svelte';
  import ChallengeSolution from '$lib/components/ChallengeSolution.svelte';
  import FullWidthImage from '$lib/components/FullWidthImage.svelte';
  import Quote from '$lib/components/Quote.svelte';
  import { projects, getAdjacentProjects } from '$lib/projects';

  let scrollY = 0;
  let translateY = 0;
  
  // Make the parallax effect reactive
  $: translateY = scrollY * 0.5;
  
  let titleContent: HTMLElement;
  let prototypesCount = 0;
  let monthsCount = 0;
  let exhibitionsCount = 0;

  // Get the current project slug from the URL
  const currentSlug = 'known-unknowns';
  const navigation = getAdjacentProjects(currentSlug);

  onMount(() => {
    // Setup number animations
    const metricsObserver = new IntersectionObserver((entries) => {
      entries.forEach(entry => {
        if (entry.isIntersecting) {
          animateValue(0, 3, 2000, (val) => prototypesCount = val);
          animateValue(0, 18, 1000, (val) => monthsCount = val);
          animateValue(0, 5, 1500, (val) => exhibitionsCount = val);
          metricsObserver.disconnect();
        }
      });
    });

    const metricsSection = document.querySelector('.metrics-section');
    if (metricsSection) {
      metricsObserver.observe(metricsSection);
    }

    // Setup scroll animations
    setupProjectAnimations();
  });
</script>

<svelte:window bind:scrollY />

<div class="min-h-screen bg-white">
  <Nav />
  <ProjectHeroSplit
    title="The Known Unknowns"
    client="Royal College of Art"
    industry="Speculative Design"
    product="Harvest personal true random numbers for your personal passwords, simulations, and desire to control unpredictable events"
    heroMedia={{
      type: 'image',
      src: "/projects/the-known-unknowns/Dungeness-4869.webp"
    }}
    metrics={[
      { value: 3, label: "Devices Built" },
      { value: 18, label: "Months Research" },
      { value: 5, label: "Exhibitions" }
    ]}
    roles="Speculative design, randomness, probability, and philosophy research."
  />

  <!-- Content -->
  <div class="project-section">
    <!-- Large Text Block -->
    <div class="space-y-2 mb-12 animate-on-view border-t border-b border-black/10 py-6" data-delay="1">
      <p class="large-text text-black/90">
        Harvest personal true random numbers for your personal passwords, simulations, and desire to control unpredictable events.
      </p>
    </div>
    <ChallengeSolution
      challengeText="In a world obsessed with quantification and risk management, this project critiques our compulsion to control uncertainty. It invites users to engage with randomness as a productive force by offering tools that generate random numbers through physical phenomena."
      solutionText="The project connects historical methods of randomness generation with modern-day simulations such as Monte Carlo methods, emphasizing probability as narrative."
    />

    <div class="animate-on-view bg-gray-50 rounded-lg shadow-md px-8 py-12 my-12 max-w-3xl mx-auto">
      <div class="relative z-10">
        <h2 class="text-2xl">Philosophical Framework</h2>
        <p class="mb-8 text-base text-gray-700">
          Positioned between David Hume's deterministic skepticism and Quentin Meillassoux's radical contingency, the work embraces the ambiguity of probabilistic systems.
        </p>
        <p class="text-base text-gray-700">
          The project doesn't resolve this tension—it lives within it, offering a space for contemplation about the nature of randomness and our relationship with uncertainty.
        </p>
      </div>
    </div>

    <div class="container border-t border-black/10 mx-auto px-8 pt-16">
      <!-- Text Section -->
      <div class="mb-16 animate-on-view">
        <div class="grid grid-cols-12 gap-4">
          <div class="col-span-12">
            <h2 class="text-3xl font-light mb-12">The Devices</h2>
          </div>
          <div class="col-span-12">
            <h3 class="text-xl font-light mb-4">Cosmic Ray Chamber</h3>
          </div>
        </div>
        <div class="col-span-12">
          <p class="text-black/90 leading-relaxed col-start-2 col-span-10">
            A device that generates true randomness by detecting cosmic radiation through a cloud chamber. Each cosmic ray interaction creates a unique pattern, which is then translated into random numbers.
          </p>
        </div>
      </div>

      <div class="w-full mx-auto mb-16">
        <VideoPlayer
          src="https://vz-6b226b53-3c8.b-cdn.net/e67026e0-5fa8-40bb-996e-855557b6a2d4/playlist.m3u8"
          autoplay={true}
          muted={true}
          loop={true}
          controls={true}
        />
      </div>

      <div class="flex flex-col md:flex-row items-center gap-8 my-16 animate-on-view">
        <div class="w-full md:w-1/2">
          <img
            src="/projects/the-known-unknowns/Dr_Octagon 0322.jpg"
            alt="Dr Octagon device"
            class="w-full h-auto object-contain shadow-lg"
          />
        </div>
        <div class="w-full md:w-1/2 animate-on-view">
          <h3 class="text-xl font-light mb-4">Cosmic Ray Chamber</h3>
          <p class="text-gray-700">
            This device demonstrates the physical process of randomness generation, using a unique mechanism to visualize unpredictable outcomes.
          </p>
        </div>
      </div>

      <!-- Device 2: Random Event Harvester -->
      <div class="w-full my-16 animate-on-view">
        <h3 class="text-xl font-light mb-4">Random Event Harvester</h3>
        <p class="text-gray-700">
          A mobile device equipped with a Geiger counter and GPS that collects random data from background radiation. It maps the invisible landscape of radioactive decay, creating a geography of randomness.
        </p>
      </div>
      <FullWidthImage
        src="/projects/the-known-unknowns/Dungeness-4770.jpg"
        alt="Random Anemometer Device"
        caption="The Random Event Harvester is a mobile device equipped with a Geiger counter and GPS that collects random data from background radiation."
      />
    </div>

    <!-- Quote -->
    <Quote
      text="We're not just interested in where a bird might land tomorrow, but in all the places it might have visited along the way."
    />
    <div class="my-24 px-8 md:px-16 text-base text-gray-600 italic">— Nassim Nicholas Taleb</div>

    <div class="container border-t border-black/10 mx-auto px-8 pt-16">
      <!-- Device 3: Random Anemometer -->
      <div class="w-full my-16 animate-on-view">
        <h3 class="text-xl font-light mb-4">Random Anemometer</h3>
        <p class="text-gray-700">
          A mechanical device that creates pseudo-random values based on wind directions. It translates the chaotic patterns of air movement into a continuous stream of random data.
        </p>
      </div>
      <FullWidthImage
        src="/projects/the-known-unknowns/Dungeness-4919 2.jpg"
        alt="Random Anemometer Device"
        caption="The Random Anemometer is a mechanical device that creates pseudo-random values based on wind directions."
      />
    </div>

    <!-- Historical Reference -->
    <div class="my-24 px-8 md:px-16 animate-on-view">
      <div class="max-w-3xl mx-auto font-serif">
        <h2 class="text-3xl mb-12">Historical Reference</h2>
        <div class="space-y-8 text-lg leading-relaxed">
          <p>
            The title nods to the RAND Corporation's 1955 publication <em>A Million Random Digits with 100,000 Normal Deviates</em>—a compilation of numbers used in simulations across science and economics. Today, such randomness is generated by algorithms (pseudo-random) or, more authentically, by quantum-based methods.
          </p>

          <div class="grid grid-cols-1 md:grid-cols-2 gap-8 my-12">
            <figure class="animate-on-view">
              <img
                src="https://www.bitforms.art/wp-content/uploads/2020/02/rc_amrd_w.jpg"
                alt="RAND Corporation's 'A Million Random Digits' book"
                class="w-full mb-4"
              />
              <figcaption class="text-sm text-gray-600 italic">
                RAND Corporation's 1955 publication, a foundational text in computational randomness
              </figcaption>
            </figure>
            <figure class="animate-on-view">
              <img
                src="https://i.dailymail.co.uk/1s/2022/11/15/18/64577203-11430379-Ernie_the_machine_responsible_for_picking_Premium_Bonds_winners_-m-5_1668537169870.jpg"
                alt="ERNIE (Electronic Random Number Indicator Equipment)"
                class="w-full mb-4"
              />
              <figcaption class="text-sm text-gray-600 italic">
                ERNIE (Electronic Random Number Indicator Equipment), a true random number generator used for UK Premium Bonds since 1957
              </figcaption>
            </figure>
          </div>

          <p class="animate-on-view">
            The project's simulations are inspired by Monte Carlo methods, which generate "sample paths" to explore possible realities. These techniques are used not only to forecast futures but also to imagine alternate pasts, echoing Nassim Taleb's metaphor of tracking a bird's entire journey—not just its destination.
          </p>
        </div>
      </div>
    </div>

    <!-- Impact -->
    <div class="w-full animate-on-view">
      <h2 class="text-2xl font-light mt-8 mb-8 px-8">Impact</h2>
      <p class="text-lg text-gray-700 px-8 mb-12">
        By embedding complex probability logic in tactile, curious objects, The Known Unknowns transforms abstract statistical theory into something experiential and relatable. It invites users to reconsider how randomness underpins not only abstract theory but many pragmatic systems in our daily lives:
      </p>
      <div class="grid grid-cols-1 md:grid-cols-2 mb-16 bg-black">
        <FeatureCell
          icon='<svg class="w-8 h-8 mb-4" xmlns="http://www.w3.org/2000/svg" width="32" height="32" viewBox="0 0 32 32"><path fill="currentColor" d="M21 2v2h-2V2zm3 2h2v2h-2zm0-2v2h-2V2zM8 16h2v2H8zm15 0h2v2h-2zm-4 0h2v2h-2zm-7 0h2v2h-2zm3.5 0h2v2h-2zM6 16v2H4v-2zm0-4v2H4v-2zm0 8v2H4v-2zm2-12h2v2H8zm15 0h2v2h-2zm-4 0h2v2h-2zm-7 0h2v2h-2zm3.5 0h2v2h-2zM6 8v2H4V8zm0 16v2H4v-2zm2 4h2v2H8zm15 0h2v2h-2zm-4 0h2v2h-2zm-7 0h2v2h-2zm3.5 0h2v2h-2zM6 28v2H4v-2z"/></svg>'
          title="Personal PIN Generation"
          description="We rely on random or pseudo-random number generation to create secure PINs and passwords—critical to digital identity and financial security. This project highlights how even everyday tools of randomness are quietly essential."
          delay={0}
        />

        <FeatureCell
          icon='<svg class="w-8 h-8 mb-4" xmlns="http://www.w3.org/2000/svg" width="32" height="32" viewBox="0 0 32 32"><path fill="currentColor" d="M26 4.03v14.73c0 1.4-.74 2.67-1.95 3.37L16 26.72l-8.05-4.59A3.97 3.97 0 016 18.76V4.03h20m2-2H4v16.73c0 2.1 1.12 4.07 2.93 5.13l8.13 4.64c.54.31 1.15.47 1.76.47h.36c.61 0 1.22-.16 1.76-.47l8.13-4.64A6.04 6.04 0 0030 18.76V2.03z"/></svg>'
          title="Simulation of Uncertain Events"
          description="From financial crises to unpredictable disasters like 9/11, stochastic models help institutions run simulations to prepare for 'known unknowns.' The project echoes this logic by letting individuals engage with their own version of future scenario-building."
          delay={0.2}
        />

        <FeatureCell
          icon='<svg class="w-8 h-8 mb-4" xmlns="http://www.w3.org/2000/svg" width="32" height="32" viewBox="0 0 32 32"><path fill="currentColor" d="M16 2a14 14 0 1014 14A14 14 0 0016 2zm0 26a12 12 0 1112-12 12 12 0 01-12 12z"/></svg>'
          title="Premium Bonds & Lottery Systems"
          description="Governments like the UK use randomized systems (e.g., National Savings & Investments Premium Bonds) to distribute financial gains via chance—introducing randomness as a policy instrument. The Known Unknowns proposes similarly playful, non-linear engagements with fortune and reward."
          delay={0.4}
        />

        <FeatureCell
          icon='<svg class="w-8 h-8 mb-4" xmlns="http://www.w3.org/2000/svg" width="32" height="32" viewBox="0 0 32 32"><path fill="currentColor" d="M27.4 16l2.4-2.4-2.3-2.3-2.4 2.4-2.4-2.4L20.4 14l2.4 2-2.4 2.4 2.3 2.3 2.4-2.4 2.4 2.4 2.3-2.3zm-6.6 8H4V8h16.8l2-2H4a2 2 0 00-2 2v16a2 2 0 002 2h16.8z"/></svg>'
          title="Scientific Simulations"
          description="Whether modeling climate change or drug behavior in the body, scientists use probabilistic simulations (often via Monte Carlo methods) to approximate future outcomes. The project brings these highly technical methods down to earth—into the living room, onto the kitchen table."
          delay={0.6}
        />
      </div>
      <div class="my-24 px-8 md:px-16 animate-on-view">
        <p class="text-xl text-gray-700 px-8 mb-12 animate-on-view">
          In a world obsessed with precision and predictability, The Known Unknowns proposes a counterpoint: that randomness, when embraced, can be a powerful and poetic lens through which to rethink everyday decision-making.
        </p>
      </div>

      <Quote
        text="There are known knowns, things we know that we know; and there are known unknowns, things that we know we don't know. But there are also unknown unknowns, things we do not know we don't know."
      />
      <div class="my-24 px-8 md:px-16 text-base text-gray-600 italic">— Donald Rumsfeld</div>
    </div>
  </div>

  {#if navigation}
    <ProjectNav 
      currentProject={{
        title: navigation.currentProject.title,
        category: navigation.currentProject.category,
        link: navigation.currentProject.link
      }}
      allProjects={projects}
    />
  {/if}

  <Footer />
</div>
